import './ProductCategories.scss'
import React, { useEffect } from 'react'
import { Link, browserHistory } from 'react-router'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import Helmet from 'react-helmet'
import { Button, Skeleton, Alert, message } from 'antd-v4'
import Paragraph from 'antd-v4/lib/typography/Paragraph'
import { PlusOutlined } from '@ant-design/icons'
import { CategoriesModels } from '@getgreenline/categories'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { SortableCategoryRowElement } from './SortableCategoryRow'
import { AppView, AppMainView } from '../../../../components/AppView/index'
import AppHeader from '../../../../components/AppView/Header'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { HelpLinkMenu } from '../../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import {
  PermissionNames,
  IComplianceCategoryContract,
  ComplianceCategoryIdMapping,
  CreateCategory,
  ICategoryContract,
} from '@getgreenline/homi-shared'
import { parseErrorMsg } from '../../../../utilities/helpers'
import classNames from 'classnames'
import { isNil } from 'lodash'
import { CATEGORY_COLOURS } from '../../../../constants/Colors'
import { HelpLinks } from '../../../../constants/ExternalLinks'

enum ExtraCategories {
  PRE_ROLLS = 'Pre-rolls',
  ACCESSORIES = 'Accessories',
}

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

const _ProductCategories: React.FC<React.PropsWithChildren<Props>> = ({
  currentCompanyStore,
  currentUserStore,
  children,
}) => {
  const productStore = currentCompanyStore?.productStore
  const company = currentCompanyStore?.company
  if (!productStore || !company) return null
  const categories = productStore.categories
  const canEditCategories = !!currentUserStore?.hasPermissionAtEntity(
    PermissionNames.CAN_EDIT_CATEGORIES,
    company.id,
  )

  const reorderDefaultCategories = (categories: ICategoryContract[]) => {
    const categoryIndexMapping = categories
      .sort((a, b) =>
        b.categoryType === CategoriesModels.CategoryType.SYSTEM_FEES
          ? -1
          : a.name.localeCompare(b.name),
      )
      .map((category, index) => ({
        categoryId: category.id,
        index: index,
      }))
    return categoryIndexMapping
  }

  const createCategoryObject = (
    defaultCategory:
      | IComplianceCategoryContract
      | { name: ExtraCategories; id?: ComplianceCategoryIdMapping },
  ) => {
    const categoryObj = new CreateCategory()
    categoryObj.name = defaultCategory.name
    categoryObj.complianceCategoryId = defaultCategory.id
    if (defaultCategory.name === ExtraCategories.ACCESSORIES) {
      categoryObj.colorHex = CATEGORY_COLOURS.ACCESSORIES
    } else if (defaultCategory.name === ExtraCategories.PRE_ROLLS) {
      categoryObj.colorHex = CATEGORY_COLOURS.PRE_ROLLS
    } else {
      if (isNil(defaultCategory.id)) return
      const complianceCategoryKey = ComplianceCategoryIdMapping[defaultCategory.id]
      categoryObj.colorHex = CATEGORY_COLOURS[complianceCategoryKey]
    }
    return categoryObj
  }

  const complianceCategories = (productStore.complianceCategories || []).filter(
    ({ id: complianceId }) =>
      ![ComplianceCategoryIdMapping.OIL, ComplianceCategoryIdMapping.AGCO_ACCESSORY].includes(
        complianceId,
      ),
  )

  const defaultCategories = [
    ...complianceCategories,
    { name: ExtraCategories.PRE_ROLLS, id: ComplianceCategoryIdMapping.DRIED },
    { name: ExtraCategories.ACCESSORIES },
  ].map((defaultCategory) => createCategoryObject(defaultCategory))

  const addDefaultCategories = async () => {
    try {
      for (const category of defaultCategories) {
        await productStore.addCategory(category)
      }
      await productStore.getCategories()
      const categoryIndexMapping = reorderDefaultCategories(productStore.categories || [])
      await productStore.updateCategoryOrder(categoryIndexMapping)
      message.success('Successfully added compliance categories.')
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error adding compliance categories.')
    }
  }

  const openAddCategoryModal = () => {
    const addCategoryUrl = `${window.location.pathname}/add-category`.replace('//', '/')
    browserHistory.push(addCategoryUrl)
  }

  useEffect(() => {
    productStore.getCategories()
    productStore.getComplianceCategories()
  }, [])

  const AddAllComplianceCategories = () => (
    <div className='text-center'>
      <Paragraph className='text-muted'>
        You have not added any categories yet.
        <br />
        Would you like to add all compliance categories?
      </Paragraph>
      <Button type='primary' icon={<PlusOutlined />} onClick={addDefaultCategories}>
        Add compliance categories
      </Button>
      {children}
    </div>
  )

  const CategoriesTable = () => (
    <div className='table-responsive'>
      <table className='table'>
        <thead className='thead'>
          <tr>
            <th>Order</th>
            <th>Color</th>
            <th>Name</th>
            <th>Compliance</th>
            <th>Options</th>
          </tr>
        </thead>
        <tbody>
          {categories?.map((category, index) => (
            <SortableCategoryRowElement
              canEditCategories={canEditCategories}
              key={index}
              index={index}
              category={category}
            />
          ))}
        </tbody>
      </table>
      {children}
    </div>
  )

  const MainContent = () => {
    if (categories === undefined) {
      return <Skeleton active />
    } else if ((categories || []).length <= 1) {
      return <AddAllComplianceCategories />
    }
    return <CategoriesTable />
  }

  return (
    <AppView column>
      <Helmet>
        <title>Products | Categories</title>
        <meta name='page-loaded' content='true' />
      </Helmet>
      <AppHeader title='Categories'>
        {canEditCategories && (
          <span className='spaced-button-wrapper'>
            <HelpLinkMenu
              helpLinkMenus={{
                href: HelpLinks.PRODUCTS_CATEGORIES,
                title: 'Adding categories',
              }}
            />

            <BlazeButton
              buttonProps={{
                type: 'primary',
                icon: 'plus',
                onClick: openAddCategoryModal,
              }}
              titleProps={{
                title: 'Add category',
              }}
            />
          </span>
        )}
      </AppHeader>
      <AppMainView>
        <div className='row'>
          <main
            className={classNames(
              'col-lg-6 mb-4',
              (categories || []).length === 0 && 'd-flex align-items-center justify-content-center',
            )}
          >
            <MainContent />
          </main>
          <article className='col-lg-6'>
            <Alert
              type='info'
              message='Category recommendations'
              description={
                <ul style={{ paddingLeft: 20 }}>
                  <li className='mt-2'>
                    <Paragraph className='mb-1'>Accessories</Paragraph>
                    <Paragraph className='text-muted indent-20'>
                      Subcategories: Rolling papers, Grinders, Bongs, Pipes, Lighters
                      <br />
                      Compliance: None is needed. Select "Does not contain cannabis"
                    </Paragraph>
                  </li>
                  <li>
                    <Paragraph className='mb-1'>Beverages</Paragraph>
                    <Paragraph className='text-muted indent-20'>
                      Subcategories: THC, CBD, 1:1
                      <br />
                      Compliance: Edibles non-solids
                    </Paragraph>
                  </li>
                  <li>
                    <Paragraph className='mb-1'>Dried flower</Paragraph>
                    <Paragraph className='text-muted indent-20'>
                      Subcategories: Sativa, Hybrid, Indica, CBD
                      <br />
                      Compliance: Dried flower
                    </Paragraph>
                  </li>
                  <li>
                    <Paragraph className='mb-1'>Edibles</Paragraph>
                    <Paragraph className='text-muted indent-20'>
                      Subcategories: Chocolate, Gummies/Candies, Baked goods, Teas
                      <br />
                      Compliance: Edibles solids
                    </Paragraph>
                  </li>
                  <li>
                    <Paragraph className='mb-1'>Extracts ingested</Paragraph>
                    <Paragraph className='text-muted indent-20'>
                      Subcategories: Capsules, Oils/Tinctures, Sprays
                      <br />
                      Compliance: Extracts ingested
                    </Paragraph>
                  </li>
                  <li>
                    <Paragraph className='mb-1'>Extracts inhaled</Paragraph>
                    <Paragraph className='text-muted indent-20'>
                      Subcategories: Vape pens, Vape cartridges
                      <br />
                      Compliance: Extracts inhaled
                    </Paragraph>
                  </li>
                  <li>
                    <Paragraph className='mb-1'>Pre-rolls</Paragraph>
                    <Paragraph className='text-muted indent-20'>
                      Subcategories: Sativa, Hybrid, Indica, CBD
                      <br />
                      Compliance: Dried flower
                    </Paragraph>
                  </li>
                  <li>
                    <Paragraph className='mb-1'>Topicals</Paragraph>
                    <Paragraph className='text-muted indent-20'>
                      Subcategories: Creams
                      <br />
                      Compliance: Topicals
                    </Paragraph>
                  </li>
                  <br />
                  <div>
                    <Link to={HelpLinks.PRODUCTS_CATEGORIES} target='_blank' rel='noreferrer'>
                      Learn more about categories
                    </Link>
                  </div>
                </ul>
              }
              showIcon
              style={{ marginBottom: 5 }}
            />
          </article>
        </div>
      </AppMainView>
    </AppView>
  )
}

export const ProductCategories = inject(
  'currentCompanyStore',
  'currentUserStore',
)(observer(_ProductCategories))
